import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Switch,
  Textarea,
} from '@safe-minds-ai/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPlan, fetchPlans, updatePlan, Plan, CreatePlanDto, UpdatePlanDto } from '../api';
import { Pencil, Plus, Search, SlidersHorizontal } from 'lucide-react';
import { useToast } from '../toast';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_IMAGE_GENERATION_PLAN_FEATURES = {
  available: true,
} as const;

const DEFAULT_IMAGE_GENERATION_PLAN_LIMITS = {
  maxPerHour: 3,
  maxPerDay: 10,
} as const;

const DEFAULT_MODEL_LABEL = 'gpt-4o-mini';

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined) return 'Free';
  if (value === 0) return 'Free';
  return `$${value.toFixed(2)}/mo`;
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

type PlanFormState = {
  id?: string;
  name: string;
  description: string;
  priceMonth: number | '';
  childrenMax: number | '';
  chatRetentionDays: number | '';
  timeLimitEnabled: boolean;
  antiCheatingEnabled: boolean;
  antiCheatingThreshold: number | '';
  imageGenerationAvailable: boolean;
  imageGenerationMaxPerHour: number | '';
  imageGenerationMaxPerDay: number | '';
};

const emptyPlanForm = (): PlanFormState => ({
  name: '',
  description: '',
  priceMonth: '',
  childrenMax: '',
  chatRetentionDays: '',
  timeLimitEnabled: false,
  antiCheatingEnabled: false,
  antiCheatingThreshold: '',
  imageGenerationAvailable: DEFAULT_IMAGE_GENERATION_PLAN_FEATURES.available,
  imageGenerationMaxPerHour: DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerHour,
  imageGenerationMaxPerDay: DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerDay,
});

const mapPlanToForm = (plan: Plan): PlanFormState => {
  const features = (plan.features ?? {}) as Record<string, unknown>;
  const limits = (plan.limits ?? {}) as Record<string, unknown>;
  const antiCheating = (features.antiCheating ?? {}) as Record<string, unknown>;
  const imageGeneration = (features.imageGeneration ?? {}) as Record<string, unknown>;
  const imageGenerationLimits = (limits.imageGeneration ?? {}) as Record<string, unknown>;

  return {
    id: plan.id,
    name: plan.name ?? '',
    description: typeof features.description === 'string' ? features.description : '',
    priceMonth: typeof plan.price_month === 'number' ? plan.price_month : '',
    childrenMax: typeof limits.childrenMax === 'number' ? limits.childrenMax : '',
    chatRetentionDays: typeof features.chatRetentionDays === 'number' ? features.chatRetentionDays : '',
    timeLimitEnabled:
      typeof (plan.limits as Record<string, unknown> | undefined)?.timeLimitEnabled === 'boolean'
        ? ((plan.limits as Record<string, unknown>).timeLimitEnabled as boolean)
        : typeof plan.timeLimitEnabled === 'boolean'
          ? plan.timeLimitEnabled
          : false,
    antiCheatingEnabled:
      typeof antiCheating.available === 'boolean'
        ? antiCheating.available
        : typeof antiCheating.enabled === 'boolean'
          ? antiCheating.enabled
          : false,
    antiCheatingThreshold: typeof antiCheating.threshold === 'number' ? antiCheating.threshold : '',
    imageGenerationAvailable:
      typeof imageGeneration.available === 'boolean'
        ? imageGeneration.available
        : DEFAULT_IMAGE_GENERATION_PLAN_FEATURES.available,
    imageGenerationMaxPerHour:
      typeof imageGenerationLimits.maxPerHour === 'number'
        ? imageGenerationLimits.maxPerHour
        : DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerHour,
    imageGenerationMaxPerDay:
      typeof imageGenerationLimits.maxPerDay === 'number'
        ? imageGenerationLimits.maxPerDay
        : DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerDay,
  };
};

const buildPlanPayload = (form: PlanFormState): { create: CreatePlanDto; update: UpdatePlanDto } => {
  const limits: Record<string, unknown> = {};
  if (form.childrenMax !== '') limits.childrenMax = form.childrenMax;
  limits.timeLimitEnabled = form.timeLimitEnabled;
  limits.imageGeneration = {
    maxPerHour:
      form.imageGenerationMaxPerHour === ''
        ? DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerHour
        : form.imageGenerationMaxPerHour,
    maxPerDay:
      form.imageGenerationMaxPerDay === ''
        ? DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerDay
        : form.imageGenerationMaxPerDay,
  };

  const features: Record<string, unknown> = {};
  if (form.description.trim()) features.description = form.description.trim();
  if (form.chatRetentionDays !== '') features.chatRetentionDays = form.chatRetentionDays;
  if (form.antiCheatingEnabled) {
    features.antiCheating = {
      available: true,
      enabled: true,
      threshold: form.antiCheatingThreshold !== '' ? form.antiCheatingThreshold : undefined,
    };
  } else {
    features.antiCheating = { available: false, enabled: false };
  }
  features.imageGeneration = {
    available: form.imageGenerationAvailable,
  };

  const defaults: Record<string, unknown> = {
    guidance: { mandatory: true },
  };

  const payload = {
    name: form.name.trim(),
    price_month: form.priceMonth === '' ? undefined : form.priceMonth,
    timeLimitEnabled: form.timeLimitEnabled,
    features,
    limits,
    defaults,
  };

  return {
    create: payload,
    update: payload,
  };
};

export default function PlansRoute(): JSX.Element {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formState, setFormState] = useState<PlanFormState>(emptyPlanForm());
  const [pending, setPending] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const toast = useToast();

  const getPlans = useCallback(async () => {
    try {
      const data = await fetchPlans();
      setPlans(data);
    } catch (err: any) {
      setError('Failed to fetch plans.');
      toast.push({ title: 'Error', description: err.response?.data?.message || 'Failed to fetch plans.', variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    getPlans();
  }, [getPlans]);

  useEffect(() => {
    if (!dialogOpen) {
      setEditingPlan(null);
      setFormState(emptyPlanForm());
    }
  }, [dialogOpen]);

  const filteredPlans = useMemo(() => {
    const term = search.trim().toLowerCase();
    return plans.filter((plan) => {
      const features = (plan.features ?? {}) as Record<string, unknown>;
      const isActive = typeof features.isActive === 'boolean' ? features.isActive : true;
      if (statusFilter === 'active' && !isActive) return false;
      if (statusFilter === 'inactive' && isActive) return false;
      if (!term) return true;
      return [plan.name, plan.id].some((value) => value?.toLowerCase().includes(term));
    });
  }, [plans, search, statusFilter]);

  const openCreateDialog = () => {
    setEditingPlan(null);
    setFormState({ ...emptyPlanForm() });
    setDialogOpen(true);
  };

  const openEditDialog = (plan: Plan) => {
    setEditingPlan(plan);
    setFormState(mapPlanToForm(plan));
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.name.trim()) return;
    if (formState.antiCheatingEnabled && formState.antiCheatingThreshold === '') {
      toast.push({ title: 'Missing value', description: 'Anti-cheating threshold is required when enabled.', variant: 'error' });
      return;
    }
    if (formState.imageGenerationAvailable && formState.imageGenerationMaxPerHour === '') {
      toast.push({ title: 'Missing value', description: 'Hourly image limit is required when image generation is enabled.', variant: 'error' });
      return;
    }
    if (formState.imageGenerationAvailable && formState.imageGenerationMaxPerDay === '') {
      toast.push({ title: 'Missing value', description: 'Daily image limit is required when image generation is enabled.', variant: 'error' });
      return;
    }

    const payload = buildPlanPayload(formState);
    setPending(true);
    try {
      if (editingPlan) {
        await updatePlan(editingPlan.id, payload.update);
        toast.push({ title: 'Plan updated', description: `${formState.name} saved.`, variant: 'success' });
      } else {
        await createPlan({ id: uuidv4(), ...payload.create });
        toast.push({ title: 'Plan created', description: `${formState.name} added.`, variant: 'success' });
      }
      await getPlans();
      setDialogOpen(false);
    } catch (err: any) {
      toast.push({ title: 'Error', description: err.response?.data?.message || 'Failed to save plan.', variant: 'error' });
    } finally {
      setPending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary/70">Plans</p>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Subscription & Limits</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300">Configure plan entitlements and limits.</p>
        </div>
        <p>Loading plans...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary/70">Plans</p>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Subscription & Limits</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300">Configure plan entitlements and limits.</p>
        </div>
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border border-slate-200/60 bg-white/90 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-slate-700/50 dark:bg-slate-900/70">
        <CardHeader className="gap-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle className="text-2xl">Plans</CardTitle>
              <CardDescription>Configure plan entitlements and usage limits.</CardDescription>
            </div>
            <Button className="h-11 rounded-full px-6 shadow-sm" onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" /> Add New Plan
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-[360px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search plans"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 w-full rounded-full border-slate-200/70 bg-white pl-9 shadow-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-slate-400" />
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as 'all' | 'active' | 'inactive')}>
                <SelectTrigger className="h-10 w-[170px] rounded-full">
                  <SelectValue placeholder="All plans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All plans</SelectItem>
                  <SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="inactive">Inactive only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="relative space-y-4 overflow-hidden rounded-3xl">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-60 [background-image:radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.2)_1px,transparent_0)] [background-size:22px_22px] dark:opacity-30"
          />
          <div className="hidden lg:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_140px] gap-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            <span>Plan</span>
            <span>Price</span>
            <span>Children</span>
            <span>Time limits</span>
            <span>Updated</span>
            <span>Actions</span>
          </div>
          <div className="relative space-y-3">
            {filteredPlans.map((plan) => {
              const features = (plan.features ?? {}) as Record<string, unknown>;
              const limits = (plan.limits ?? {}) as Record<string, unknown>;
              const imageGeneration = (features.imageGeneration ?? {}) as Record<string, unknown>;
              const imageGenerationLimits = (limits.imageGeneration ?? {}) as Record<string, unknown>;
              const isActive = typeof features.isActive === 'boolean' ? features.isActive : true;
              const timeLimitsEnabled =
                typeof (plan.limits as Record<string, unknown> | undefined)?.timeLimitEnabled === 'boolean'
                  ? ((plan.limits as Record<string, unknown>).timeLimitEnabled as boolean)
                  : typeof plan.timeLimitEnabled === 'boolean'
                    ? plan.timeLimitEnabled
                    : false;
              return (
                <div
                  key={plan.id}
                  className="grid gap-4 rounded-3xl border border-slate-200/60 bg-white px-6 py-5 text-sm text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.65),0_18px_40px_rgba(15,23,42,0.08)] transition hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_24px_48px_rgba(15,23,42,0.12)] dark:border-slate-700/50 dark:bg-slate-900/70 dark:text-slate-200 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr_140px]"
                >
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-slate-900 dark:text-white">{plan.name}</span>
                      <Badge variant={isActive ? 'default' : 'outline'} className="rounded-full px-3">
                        {isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400">ID {plan.id}</p>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-slate-900 dark:text-white">{formatCurrency(plan.price_month)}</span>
                  </div>
                  <div className="flex items-center">
                    <span>{limits.childrenMax ?? '—'} child max</span>
                  </div>
                  <div className="flex items-center">
                    <div className="flex flex-col gap-2">
                      <Badge variant={timeLimitsEnabled ? 'default' : 'outline'} className="rounded-full px-3">
                        {timeLimitsEnabled ? 'Time limits on' : 'Time limits off'}
                      </Badge>
                      <Badge
                        variant={imageGeneration.available === false ? 'outline' : 'default'}
                        className="rounded-full px-3"
                      >
                        {imageGeneration.available === false
                          ? 'Images off'
                          : `${imageGenerationLimits.maxPerHour ?? DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerHour}/hr · ${imageGenerationLimits.maxPerDay ?? DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerDay}/day`}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span>{formatDate(plan.updatedAt)}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl shadow-sm hover:shadow-md"
                      onClick={() => openEditDialog(plan)}
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" /> Edit
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          {filteredPlans.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200/70 py-10 text-center text-sm text-slate-500 dark:border-slate-700/60">
              No plans found.
            </div>
          ) : (
            <p className="text-sm text-slate-500">Showing {filteredPlans.length} {filteredPlans.length === 1 ? 'result' : 'results'}</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit plan' : 'Add new plan'}</DialogTitle>
            <DialogDescription>Keep plan configuration clear and structured for parents.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 py-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Plan name</Label>
                <Input
                  value={formState.name}
                  onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Family Free"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Price per month</Label>
                <Input
                  type="number"
                  value={formState.priceMonth}
                  onChange={(e) => setFormState((prev) => ({ ...prev, priceMonth: parseFloat(e.target.value) || '' }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formState.description}
                  onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Short summary for admins and parents"
                  rows={2}
                />
              </div>
              <div className="rounded-2xl border border-slate-200/60 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-700/50 dark:bg-slate-900/60 dark:text-slate-300 md:col-span-2">
                Plans use the default model <span className="font-semibold text-slate-900 dark:text-slate-100">{DEFAULT_MODEL_LABEL}</span>.
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold">Limits</h3>
                <p className="text-sm text-slate-500 dark:text-slate-300">Control learner caps and retention settings.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Max children per household</Label>
                  <Input
                    type="number"
                    value={formState.childrenMax}
                    onChange={(e) => setFormState((prev) => ({ ...prev, childrenMax: parseInt(e.target.value, 10) || '' }))}
                    placeholder="1"
                    min={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Chat retention (days)</Label>
                  <Input
                    type="number"
                    value={formState.chatRetentionDays}
                    onChange={(e) => setFormState((prev) => ({ ...prev, chatRetentionDays: parseInt(e.target.value, 10) || '' }))}
                    placeholder="30"
                    min={1}
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold">Image generation</h3>
                <p className="text-sm text-slate-500 dark:text-slate-300">Set default availability and spend-control limits for this plan.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between rounded-2xl border border-slate-200/60 bg-slate-50 px-4 py-3 dark:border-slate-700/50 dark:bg-slate-900/60 md:col-span-2">
                  <div>
                    <p className="text-sm font-semibold">Enable image generation by default</p>
                    <p className="text-xs text-slate-500 dark:text-slate-300">Children inherit this as enabled unless a parent disables it for a specific child.</p>
                  </div>
                  <Switch
                    checked={formState.imageGenerationAvailable}
                    onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, imageGenerationAvailable: checked }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default hourly image limit</Label>
                  <Input
                    type="number"
                    value={formState.imageGenerationMaxPerHour}
                    onChange={(e) => setFormState((prev) => ({ ...prev, imageGenerationMaxPerHour: parseInt(e.target.value, 10) || '' }))}
                    placeholder={String(DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerHour)}
                    min={1}
                    max={100}
                    disabled={!formState.imageGenerationAvailable}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default daily image limit</Label>
                  <Input
                    type="number"
                    value={formState.imageGenerationMaxPerDay}
                    onChange={(e) => setFormState((prev) => ({ ...prev, imageGenerationMaxPerDay: parseInt(e.target.value, 10) || '' }))}
                    placeholder={String(DEFAULT_IMAGE_GENERATION_PLAN_LIMITS.maxPerDay)}
                    min={1}
                    max={1000}
                    disabled={!formState.imageGenerationAvailable}
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold">Time limits</h3>
                <p className="text-sm text-slate-500 dark:text-slate-300">Enable daily time limit controls for each child.</p>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200/60 bg-slate-50 px-4 py-3 dark:border-slate-700/50 dark:bg-slate-900/60">
                <div>
                  <p className="text-sm font-semibold">Enable time limits</p>
                  <p className="text-xs text-slate-500 dark:text-slate-300">Parents can set a daily limit per child.</p>
                </div>
                <Switch
                  checked={formState.timeLimitEnabled}
                  onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, timeLimitEnabled: checked }))}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold">Safety & moderation</h3>
                <p className="text-sm text-slate-500 dark:text-slate-300">Control anti-cheating defaults for this plan.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between rounded-2xl border border-slate-200/60 bg-slate-50 px-4 py-3 dark:border-slate-700/50 dark:bg-slate-900/60 md:col-span-2">
                  <div>
                    <p className="text-sm font-semibold">Enable anti-cheating</p>
                    <p className="text-xs text-slate-500 dark:text-slate-300">Allows parent to toggle anti-cheat per child.</p>
                  </div>
                  <Switch
                    checked={formState.antiCheatingEnabled}
                    onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, antiCheatingEnabled: checked }))}
                  />
                </div>
                {formState.antiCheatingEnabled && (
                  <div className="space-y-2">
                    <Label>Cheating similarity threshold</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      max={1}
                      value={formState.antiCheatingThreshold}
                      onChange={(e) => setFormState((prev) => ({ ...prev, antiCheatingThreshold: parseFloat(e.target.value) || '' }))}
                      placeholder="0.5"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-300">
                      Sets the default threshold for new children. Parents can adjust per child later.
                    </p>
                  </div>
                )}
                <div className="rounded-2xl border border-slate-200/60 bg-slate-50 px-4 py-3 text-xs text-slate-500 dark:border-slate-700/50 dark:bg-slate-900/60 dark:text-slate-300 md:col-span-2">
                  Guidance responses are always enforced across all plans.
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:justify-end">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? 'Saving...' : editingPlan ? 'Save changes' : 'Create plan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
